import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { map, Observable, Subscription } from "rxjs";
import { StaffApiService } from "./core/api/services";
import { Router } from "@angular/router";
import { AuthService } from "./auth/auth.service";
import { PatientServiceSparkModel, PatientSessionModel, Permissions } from "./core/api/models";
import { AlertService } from "./shared/services/alert.service";
import { OfflineDataService } from "./core/services/offline-data.service";
import { Utils } from "./core/utils";
import { FrontendDiagnosticsService } from './core/services/frontend-diagnostics.service';
@Component({
  selector: 'app-select-patient',
  templateUrl: './select-patient.component.html',
  standalone: false,
})
export class SelectPatientComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  private servicesRequest?: Subscription;
  serviceDate: string;
  services: PatientServiceSparkModel[] = [];
  servicesLoading = false;
  servicesError: string | null = null;
  isOffline = false;
  creatingService: PatientServiceSparkModel | null = null;

  selectedPatient: { ID: number | undefined, FullName: string } | null = null;
  patientID: number | null = null;
  loading: boolean = true;
  patientOptions: { ID: number | undefined, FullName: string, searchString: string }[] = [];
  constructor(
    private staffApiService: StaffApiService,
    private router: Router,
    private authService: AuthService,
    private alertService: AlertService,
    private offlineDataService: OfflineDataService,
    private diagnostics: FrontendDiagnosticsService
  ) {
    const today = new Date();
    this.serviceDate = [today.getFullYear(), String(today.getMonth() + 1).padStart(2, '0'), String(today.getDate()).padStart(2, '0')].join('-');
  }

  ngOnInit(): void {
    this.offlineDataService.getOfflineModeStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOffline) => {
        this.isOffline = isOffline;
        this.loadServices();
      });
    this.diagnostics.recordEntry('patient-selector-opened', { HasAccessToken: this.authService.isSignedIn() });

    if (!this.patientOptions || this.patientOptions.length === 0) {
      this.authService.getMyPatients().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((patients) => {
        if (patients) {
          this.patientOptions = patients.map(patient => ({
            ID: patient.ID,
            FullName: `${patient.FirstName} ${patient.LastName}`,
            searchString: `${patient.FirstName} ${patient.LastName} ${patient.PreferredName ?? ''} ${patient.FirstName?.toLowerCase().substr(0, 3)}${patient.LastName?.toLowerCase().substr(0, 3)}`.toLowerCase()
          }));
        }
        this.loading = false;
      },
        (error) => {
          this.loading = false;
          this.alertService.error('Error loading patients: ' + error.message);
        }
      );
    } else {

      this.loading = false;
    }
  }

  loadServices(date: string = this.serviceDate): void {
    this.serviceDate = date;
    this.servicesRequest?.unsubscribe();
    this.services = [];
    this.servicesLoading = false;
    this.servicesError = null;
    if (this.isOffline) {
      return;
    }
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      this.servicesError = 'Select a valid service date.';
      return;
    }

    this.servicesLoading = true;
    this.servicesRequest = this.staffApiService.GetMyServices({ date })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (services) => {
          this.services = services;
          this.servicesLoading = false;
        },
        error: (error) => {
          const message = Utils.getAjaxError(error);
          this.servicesError = 'Error loading services: ' + (message || 'Please try again.');
          this.servicesLoading = false;
        }
      });
  }

  changedPatient(event: { originalEvent?: Event, value: number }) {
    const newPatientIDSelected = event.value;
    if (!Number.isInteger(newPatientIDSelected) || newPatientIDSelected <= 0) {
      return;
    }
    this.selectedPatient = this.patientOptions?.find(patient => patient.ID === newPatientIDSelected) ?? null;
    this.diagnostics.recordEntry('patient-selected', {
      HasAccessToken: this.authService.isSignedIn(),
      AccessExpiresAt: this.authService.tokenInfo?.expires_at,
      AccessRemainingMs: this.authService.accessTokenRemainingMs(),
    });

    if (this.authService.userInfo?.Permissions?.includes(Permissions.SparkApp_Supervisor)) {
      this.router.navigate(['/supervisor/' + newPatientIDSelected + '/overview']);
    } else {
      this.router.navigate(['/staff/' + newPatientIDSelected + '/sessions']);
    }
  }

  getServiceURL(service: PatientServiceSparkModel): string | null {
    const optoURL = this.authService.getOptoURL();
    return optoURL && service.ID
      ? optoURL.replace(/\/+$/, '') + '/DailyTreatment/Services/View/' + service.ID
      : null;
  }

  createSessionForService(service: PatientServiceSparkModel): void {
    if (this.creatingService || this.isOffline) {
      return;
    }
    if (!service.ShowCreateSession || !service.CanCreateSession || service.LinkedSessionID || !service.ID || !service.PatientID) {
      this.alertService.error(service.SessionActionUnavailableReason || 'Refresh services before creating a session.');
      return;
    }

    this.creatingService = service;
    const patientIDs = service.CreationPatientIDs ?? [service.PatientID];
    const createRequest: Observable<Partial<PatientSessionModel>> = patientIDs.length > 1
      ? this.staffApiService.CreateGroupSession({ body: {
          ServiceID: service.ID, PatientIDs: patientIDs,
          SparkModeID: service.CreationSparkModeID
        } }).pipe(map(group => {
          const member = group.Members?.find(member => member.PatientID === service.PatientID);
          return { ID: member?.SessionID, GroupSessionID: group.ID, SparkModeID: group.SparkModeID, FinishedDate: member?.FinishedDate };
        }))
      : this.staffApiService.SavePatientSession({
      body: { PatientID: service.PatientID, ServiceID: service.ID },
      confirmSessionSwitch: false, useServiceSparkMode: true
    });
    createRequest.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (session) => {
        this.creatingService = null;
        if (!session?.ID) {
          this.alertService.error('The session could not be opened. Refresh services to check its status.');
          this.loadServices();
          return;
        }
        service.LinkedSessionID = session.ID;
        service.GroupSessionID = session.GroupSessionID;
        service.LinkedSessionSparkModeID = session.SparkModeID;
        service.LinkedSessionFinishedDate = session.FinishedDate;
        service.CanCreateSession = false;
        service.CanOpenLinkedSession = true;
        if (this.isOffline) {
          this.alertService.info('Session created. Reconnect and refresh services to open it.');
          return;
        }
        this.openLinkedSession(service);
      },
      error: (error) => {
        this.creatingService = null;
        const message = Utils.getAjaxError(error);
        this.alertService.error('Error creating session: ' + (message || 'Please try again.'));
        this.loadServices();
      }
    });
  }

  openLinkedSession(service: PatientServiceSparkModel): void {
    if (this.creatingService || this.isOffline || !service.LinkedSessionID || !service.CanOpenLinkedSession) {
      return;
    }
    const mode = service.LinkedSessionSparkModeID ?? 0;
    const commands = service.GroupSessionID
      ? ['/staff/group', service.GroupSessionID]
      : service.LinkedSessionFinishedDate
        ? ['/staff', service.PatientID, 'sessions', service.LinkedSessionID]
        : ['/staff/home', service.PatientID, service.LinkedSessionID];
    this.router.navigate(commands, { queryParams: { sparkMode: mode, academyMode: mode === 1 ? 1 : null } });
  }

  goToOfflineData() {
    this.router.navigate(['/offline/patients']);
  }

}
